// ** Redux Imports
import { Action } from 'redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  GygUserIdentityType,
  GygUserIdentityStateType,
  GygUserGroupByIdentityProviderType,
  IdentityProvider,
  GygUserGroupByIdentityProviderAndDayType,
  GygUserDeletionType
} from 'src/types/apps/gygUserIdentityTypes'

// ** Axios Imports
import type { AxiosInstance } from 'axios'
import {
  GET_ACCOUNT_DELETION_LAST_30_DAYS_ACTION,
  GET_USER_IDENTITY_BY_USER_ID_ACTION,
  GET_USER_IDENTITY_GROUP_BY_PROVIDER_ACTION,
  GET_USER_IDENTITY_GROUP_BY_PROVIDER_AND_DAY_LAST_30_DAYS_ACTION,
  USER_IDENTITY_SLICE_NAME
} from './actions'

type AxiosMiddlewareExtras = {
  httpClient: AxiosInstance
}

// todo(jhudiel) - move to root
interface Redux {
  getState: () => any
  dispatch: ThunkDispatch<any, AxiosMiddlewareExtras, Action<string>>
  extra: AxiosMiddlewareExtras
}

interface UserIdentityResponse {
  data: GygUserIdentityType[]
}

interface UserIdentitiesGroupByIdentityProviderResponse {
  data: GygUserGroupByIdentityProviderType
}

interface UserIdentitiesGroupByIdentityProviderLast30DaysResponse {
  data: GygUserGroupByIdentityProviderAndDayType[]
  pending: boolean
  error: string | null
}

interface UserDeletionLast30DaysResponse {
  data: GygUserDeletionType
  pending: boolean
  error: string | null
}

export const getUserIdentityByUserId = createAsyncThunk<UserIdentityResponse, string, Redux>(
  GET_USER_IDENTITY_BY_USER_ID_ACTION,
  async (userId: string, { extra }) => {
    const response = await extra.httpClient(`user-identity?userId=${userId}`)

    return response.data
  }
)

// this will be used to get the count of user account by identity provider for Account Login Types chart in Customer Portal Dashboard
export const getUserIdentitiesGroupByIdentityProvider = createAsyncThunk<
  UserIdentitiesGroupByIdentityProviderResponse,
  void,
  Redux
>(GET_USER_IDENTITY_GROUP_BY_PROVIDER_ACTION, async (_, { extra }) => {
  const response = await extra.httpClient(`user-identity/providers/groupByIdentityProvider`)

  return response.data
})

// this will be used to get the count of user account by identity provider per day for last 30 days for Account Sign-ups chart in Customer Portal Dashboard
export const getUserIdentitiesGroupByIdentityProviderLast30Days = createAsyncThunk<
  UserIdentitiesGroupByIdentityProviderLast30DaysResponse,
  { identityProviders: IdentityProvider[] },
  Redux
>(GET_USER_IDENTITY_GROUP_BY_PROVIDER_AND_DAY_LAST_30_DAYS_ACTION, async ({ identityProviders }, { extra }) => {
  const queryString = `identityProvider=${identityProviders.join(',')}`
  try {
    const response = await extra.httpClient(`users/registration-stats?${queryString}`)

    return response.data
  } catch (error) {
    throw error
  }
})

// Utility function to get week number from a date
const getWeekNumber = (dateString: string) => {
  const date = new Date(dateString)
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
  const pastDaysOfYear = (+date - +firstDayOfYear) / 86400000

  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
}

// this will be used to get the count of user account deleted for last 30 days for Account Deletion chart in Customer Portal Dashboard
export const getDeletionStatsLast30Days = createAsyncThunk<UserDeletionLast30DaysResponse, void, Redux>(
  GET_ACCOUNT_DELETION_LAST_30_DAYS_ACTION,
  async (_, { extra }) => {
    try {
      const response = await extra.httpClient(`users/deletion-stats`)

      return response.data
    } catch (error) {
      throw error
    }
  }
)

const initialState: GygUserIdentityStateType = {
  gygUserIdentities: [],
  gygUserIdentity: null,
  groupByIdentityProviderData: {
    Password: 0,
    Facebook: 0,
    Google: 0,
    Apple: 0
  },
  pending: false,
  getIdentityPending: false,
  groupByIdentityProviderPending: false,
  accountChartLabels: [],
  accountChartDatasets: [],
  groupByIdentityProviderAndDayPending: false,
  signUpChartLabels: [],
  signUpChartDatasets: [],
  accountDeletionPending: false,
  accountDeletionChartLabels: [],
  accountDeletionChartDatasets: []
}

export const gygUserIdentitiesSlice = createSlice({
  name: USER_IDENTITY_SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getUserIdentityByUserId.fulfilled, (state, action) => {
        state.getIdentityPending = false
        state.gygUserIdentities = action.payload.data
      })
      .addCase(getUserIdentityByUserId.pending, state => {
        state.getIdentityPending = true
      })
      .addCase(getUserIdentityByUserId.rejected, state => {
        state.getIdentityPending = false
      })

      .addCase(getUserIdentitiesGroupByIdentityProvider.fulfilled, (state, action) => {
        state.groupByIdentityProviderPending = false
        state.groupByIdentityProviderData = action.payload.data
      })
      .addCase(getUserIdentitiesGroupByIdentityProvider.pending, state => {
        state.groupByIdentityProviderPending = true
      })
      .addCase(getUserIdentitiesGroupByIdentityProvider.rejected, state => {
        state.groupByIdentityProviderPending = false
      })

      .addCase(getUserIdentitiesGroupByIdentityProviderLast30Days.fulfilled, (state, action) => {
        state.groupByIdentityProviderAndDayPending = false

        // Process the data with week numbers and without grouping by identity providers
        const aggregatedData: { [key: number]: number } = {}

        action.payload.data.forEach(({ dateRegistered, totalRegistration }) => {
          const weekNum = getWeekNumber(dateRegistered)
          if (!aggregatedData[weekNum]) {
            aggregatedData[weekNum] = 0
          }
          aggregatedData[weekNum] += Number(totalRegistration)
        })

        // Convert aggregated data into chart format
        const chartData = {
          labels: [] as string[], // Explicitly define the type as an array of strings
          datasets: [
            {
              label: 'Total Registrations',
              data: [] as number[], // Explicitly define the type as an array of numbers
              borderColor: 'green'
            }
          ]
        }

        Object.entries(aggregatedData).forEach(([weekNum, totalRegistration]) => {
          chartData.labels.push(`Week ${weekNum}`)
          chartData.datasets[0].data.push(totalRegistration)
        })

        // Update state with the processed chart data
        state.signUpChartLabels = chartData.labels
        state.signUpChartDatasets = chartData.datasets
      })

      .addCase(getUserIdentitiesGroupByIdentityProviderLast30Days.pending, state => {
        state.groupByIdentityProviderAndDayPending = true
      })
      .addCase(getUserIdentitiesGroupByIdentityProviderLast30Days.rejected, state => {
        state.groupByIdentityProviderAndDayPending = false
      })

      .addCase(getDeletionStatsLast30Days.fulfilled, (state, action) => {
        state.accountDeletionPending = false
        state.accountDeletionChartLabels = action.payload.data.map((item: any) => item.dayDeleted)
        state.accountDeletionChartDatasets = [
          {
            label: 'Deleted',
            data: action.payload.data.map((item: { totalDeleted: any }) => item.totalDeleted)
          }
        ]
      })

      .addCase(getDeletionStatsLast30Days.pending, state => {
        state.accountDeletionPending = true
      })
      .addCase(getDeletionStatsLast30Days.rejected, state => {
        state.accountDeletionPending = false
      })
  }
})

export default gygUserIdentitiesSlice.reducer
